import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../../../components/react/spinner/spinner';
import { Navigate } from 'react-router';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import styles from './expired_email.module.scss';

const LOGIN_PATH = '/login';

interface Props {
  emailRecipient: string;
  registerUserRequest: () => void;
}

const ExpiredEmail: React.FC<Props> = ({ emailRecipient, registerUserRequest }) => {
  const [alertData, setAlertData] = useState(null);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasConflictError, setHasConflictError] = useState(false);

  if (!emailRecipient) {
    const redirectTo = window.location.pathname.replace('/expired', '');
    return <Navigate to={redirectTo} />;
  }

  /**
   * Triggers a registration user request with emailRecipient and packageShortName
   *
   * @returns {undefined}
   */
  const resendActivationEmail = async () => {
    setIsLoading(true);

    try {
      await registerUserRequest();
      setIsLoading(false);
      // This triggers redirection to /verification-sent
      setVerificationEmailSent(true);
    } catch (error) {
      setIsLoading(false);
      const conflictError = error.message === 'Email already in use';
      setHasConflictError(conflictError);

      if (!conflictError) {
        setAlertData({
          type: 'error',
          message: 'Error re-sending user registration request, please try again.',
        });
      }
    }
  };

  /**
   * @returns {undefined} - Send user to login screen
   */
  const goToLogin = () => {
    window.location.href = LOGIN_PATH;
  };

  const renderIcon = (
    <i className={`fal icon-${hasConflictError ? 'info-circle' : 'stopwatch'} ${styles.icon}`}></i>
  );

  const renderTitle = (
    <div className={`${styles.title} mb-3`}>
      {hasConflictError
        ? `You have already verified the email address for your account, ${emailRecipient}`
        : 'Your request has expired'}
    </div>
  );

  const renderSubtitle = (
    <div className={`${styles.subtitle} mb-4 text-gray`}>
      For security reasons this link is no longer valid.
    </div>
  );

  if (verificationEmailSent) {
    const redirectTo = window.location.pathname.replace('/expired', '/verification-sent');
    return <Navigate push to={redirectTo} />;
  }

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div data-testid='expired-email' className={styles.expiredEmail}>
        {renderIcon}
        {renderTitle}
        {!hasConflictError && renderSubtitle}
        <button
          data-testid='resend-activation-email'
          className='btn btn-dark w-100 mt-3 mb-3'
          onClick={hasConflictError ? goToLogin : resendActivationEmail}
        >
          {hasConflictError ? 'Log in' : 'Resend activation email'}
        </button>
        <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
      </div>
    </>
  );
};

ExpiredEmail.propTypes = {
  emailRecipient: PropTypes.string.isRequired,
  registerUserRequest: PropTypes.func.isRequired,
};

export default ExpiredEmail;
